import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap } from "@/api/dtos";
import ConfigurationSourceDataBulkEdit from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataBulkEdit";
import { ConfigurationSourceDataFormFormVersionDetails, ConfigurationSourceDataFormItemValueDetails } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormDetails";
import { ConfigurationSourceDataItemValueBulkEditSettings } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataItemValueForm";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useFormVersionConfigurations from "@/hooks/form/configuration/useFormVersionConfigurations";
import useItemValueConfigurations from "@/hooks/form/configuration/useItemValueConfigurations";
import useDebouncedValue from "@/hooks/utility/useDebouncedValue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, Fragment, useCallback, useContext, useState } from "react";
import { Link, useParams } from "react-router-dom";

export const ActionButton = styled(Button)(({ theme }) => ({ 
    borderRadius: 0,
    fontSize: 16
}));

export const ActionButtonBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between'
}));

export const ActionBox = styled(Box)(({ theme }) => ({
    top: 0, 
    bottom: 0, 
    height: '48px', 
    display: 'flex', 
    marginRight: theme.spacing(-2)
}));

const ConfigurationSourceDataItemValues: FC = () => {
    const { studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName, itemName } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata } = useContext(MetadataContext);
    const { state: { value: configuration } } = useScopedField<ConfigurationSourceDataMap>('configuration', { value: true });
    const { state: { value: trialConfiguration } } = useScopedField<ConfigurationSourceDataMap>('trialConfiguration', { value: true });

    const {
        state: debouncedConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextConfigurationUpdate
    } = useDebouncedValue(configuration, 500);

    const {
        state: debouncedTrialConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextTrialConfigurationUpdate
    } = useDebouncedValue(trialConfiguration, 500);

    const onLinkUpdate = useCallback(() => {
        doNoteDebounceNextConfigurationUpdate();
        doNoteDebounceNextTrialConfigurationUpdate();
    }, [doNoteDebounceNextConfigurationUpdate, doNoteDebounceNextTrialConfigurationUpdate])

    const itemValueData = useItemValueConfigurations(
        metadata,
        debouncedTrialConfiguration ?? undefined,
        debouncedConfiguration ?? undefined,
        trial,
        debouncedConfiguration?.configuration?.id,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemName
    );

    
    const [bulkEdit, setBulkEdit] = useState(false);

    const onBulkEditCancel = useCallback(() => {
        setBulkEdit(false);
    }, [setBulkEdit])
    
    if (!itemValueData || itemValueData.length === 0) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={AppSettings.icons.alertWarning} />}
                severity="warning"
            >
                <AlertTitle>No Item Values</AlertTitle>
                No item values were found.
            </Alert> 
        )
    }
    
    if (bulkEdit) {
        return (
            <ConfigurationSourceDataBulkEdit
                data={itemValueData}
                settings={ConfigurationSourceDataItemValueBulkEditSettings}
                onLinkUpdate={onLinkUpdate}
                onCancel={onBulkEditCancel}
            />
        )
    }
    
    return (
        <>
            <Box
                sx={{
                    my: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center'
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        minHeight: 52,
                        ml: 'auto'
                    }}
                    onClick={() => setBulkEdit(true)}
                >
                    Bulk Edit
                </Button>
            </Box>
            <Paper>
                <List disablePadding>
                    {
                        itemValueData.map((x, i, a) => (
                            <ConfigurationSourceDataFormItemValueDetails
                                key={x.itemValue}
                                itemValueData={x}
                                divider={(i + 1) < a.length}
                            />
                        ))
                    }
                </List>
            </Paper>
        </>
    )
};

export default ConfigurationSourceDataItemValues;